<template>
  <div class="product-prices">
    <template v-if="hasPricingSchedules">
      <p v-if="loggedIn">
        Please select one subscription package from the options listed below:
      </p>

      <template v-if="userMessage">
        <div class="alert alert-warning">
          <sanitize-html :dirty="userMessage"></sanitize-html>
        </div>
      </template>
      <template>
        <div v-for="(schedule, index) in productPricingSchedules"
             :key="index">
          <strong class="product-prices__period">
            Choose Subscription Type:
          </strong>
          <form @submit.prevent="logDetails">
            <div v-for="(price, i) in schedule.value"
                 :key="i"
                 class="form-group">
              <input :id="price.Id"
                     v-model="selectedPrice"
                     type="radio"
                     :value="price">
              <label :for="price.Id">
                {{ price.ProductLicenceType }} - &pound;{{ price.PriceIncVat }}
              </label>
            </div>
            <add-to-basket
              class="btn btn-accent-2"
              :product-id="productId"
              :pricing-schedule-id="selectedProductPricingScheduleId"
              :product-licence-type-id="selectedProductLicenceTypeId"
              :product-format-id="selectedProductFormatId"
              :disabled="!purchaseAllowed || selectedProductPricingScheduleId === null
                || selectedProductPricingScheduleId === undefined">
              {{ addToBasketButton }}
            </add-to-basket>
          </form>
        </div>
      </template>
    </template>
    <template v-else>
      <strong>{{ noPricingScheduleMessage }}</strong>
    </template>
  </div>
</template>
<script>
import converter from "number-to-words";
import SanitizeHtml from "../Common/SanitizeHtml.vue";

export default {
  name: "MagazineSubscription",
  components: {
    SanitizeHtml,
  },
  props: {
    productId: {
      type: Number,
      required: true,
    },
    noPricingScheduleMessage: {
      type: String,
      default: null,
    },
    loggedIn: {
      type: Boolean,
      default: false,
    },
    loginUrl: {
      type: String,
      default: "/",
    },
    groupedProductPrices: {
      type: Array,
      default: () => [],
      validator: groupedProductPrices => {
        const essentialPricingKeys = [
          "Id",
          "PricingSchedule",
          "PriceIncVat",
          "PriceExcVat",
          "PricingScheduleId",
          "ProductLicenceType",
        ];
        const isValidPrice = price => essentialPricingKeys.every( key => key in price );
        const isValidPricingSchedule = pricingSchedule => pricingSchedule.every( isValidPrice );
        return ( groupedProductPrices.every( isValidPricingSchedule )
          || false || groupedProductPrices.length === 0 );
      },
    },
  },
  data () {
    return {
      selectedPrice: {},
    };
  },
  computed: {
    userMessage () {
      let result = null;

      if ( this.productPricingSchedules && this.productPricingSchedules.length === 1 ) {
        const price = this.productPricingSchedules[0].value[0];
        if ( price.showPricingMessage ) {
          result = price.ProductPriceUserMessage;
        }
      }

      return result;
    },
    purchaseAllowed () {
      let result = false;

      if ( this.selectedPrice ) {
        result = this.selectedPrice.PurchaseAllowed;
      }

      return result;
    },
    hasUserMessage () {
      return this.userMessage !== null;
    },
    hasPricingSchedules () {
      return this.productPricingSchedules && this.productPricingSchedules.length > 0;
    },
    pricingSchedulesCount () {
      let result = [];

      this.productPricingSchedules.forEach( ( p ) => {
        result = [...result, ...p.value];
      } );

      return converter.toWords( result.length );
    },
    selectedProductPricingScheduleId () {
      const { PricingScheduleId } = this.selectedPrice;
      return PricingScheduleId;
    },
    selectedProductLicenceTypeId () {
      const { ProductLicenceTypeId } = this.selectedPrice;
      return ProductLicenceTypeId;
    },
    selectedProductFormatId () {
      const { ProductFormatId } = this.selectedPrice;
      return ProductFormatId;
    },
    productPricingSchedules () {
      return this.mapProductPricingSchedules( this.groupedProductPrices );
    },
    addToBasketButton () {
      let result = "Add to basket";

      if ( this.selectedPrice?.MetaData?.Website_BuyNowButtonText ) {
        result = this.selectedPrice.MetaData.Website_BuyNowButtonText;
      }

      return result;
    },
  },
  watch: {},
  methods: {
    mapProductPricingSchedules ( productPricingSchedules = [] ) {
      return productPricingSchedules
        .map( pricingSchedule => {
          const [firstPrice] = pricingSchedule;
          const isValidSchedule = firstPrice && firstPrice.PricingSchedule;

          let mappedPricingSchedule = null;

          if ( isValidSchedule ) {
            const mutablePricingSchedule = [...pricingSchedule].map( productPrice => ( {
              ...productPrice,
              showPricingMessage: this.showPricingMessage( productPrice ),
            } ) );

            mappedPricingSchedule = {
              key: firstPrice.PricingSchedule,
              value: mutablePricingSchedule,
            };
          }

          return mappedPricingSchedule;
        } )
        .filter( schedule => schedule != null );
    },
    showPricingMessage ( productPrice ) {
      return this.getPricingMetaData( productPrice, "Website_ShowPricingMessage" ) === "1";
    },
    getPricingMetaData ( productPrice, fieldName ) {
      return this.getObjectMetaData( productPrice, fieldName );
    },
    getObjectMetaData ( object, fieldName ) {
      let result = null;

      if ( object && object.MetaData ) {
        const metaDataItem = object.MetaData[fieldName];

        if ( metaDataItem !== undefined ) {
          result = metaDataItem;
        }
      }

      return result;
    },
  },
};
</script>
<style lang="less" scoped>
.product-prices {
  margin-top: 20px;

  &__period {
    margin-bottom: .5rem;
  }

  > * + * {
    margin-top: 1rem;
  }

  &__pricing-schedules {
    width: 100%;

    .btn {
      justify-content: space-between;
    }

    .dropdown-menu {
      width: 100%;

      > li {
        text-align: center;
      }
    }
  }

  form {
    > .form-group {
      margin-bottom: 0;

      > label {
        margin-left: 1rem;
        margin-bottom: 0;
        font-size: 1rem;
        cursor: pointer;
        font-weight: 100;
      }

      > input {
        cursor: pointer;
      }
    }

    > button {
      margin-top: 1.5rem;
    }
  }
  .alert.alert-warning{
    margin-top: 30px;
  }
  /deep/ .alert p {
    margin: 0;
  }
}
</style>
